import React from "react";
import api, { postForm } from "../../api/client";
import PdfViewer from "../../components/PdfViewer";
import { useAuth } from "../../context/AuthContext";
import PinModal from "../../components/PinModal";
import {
  DateRangePicker,
  InlineFieldInfo,
  LotSelectionTable,
  ModalShell,
  formatChecklistMonth,
  getChecklistMonthHeaderValue,
  isChecklistMonthLabel,
  isMrpHeaderLabel,
} from "../../components/ui-helpers";
import { withOrgQS } from "../../utils/url";
import { useOrg } from "../../context/OrgContext";
import { useTerminology } from "../../context/TerminologyContext";
import { formatDateTime } from "../../utils/datetime";

function numberish(x) {
  if (x == null) return null;
  const m = String(x)
    .normalize("NFKC")
    .match(/[-+]?(?:(?:\d+(?:,\d+)*)?(?:\.\d+)|\d+(?:,\d+)*)(?:[eE][-+]?\d+)?/);
  return m ? Number(m[0].replace(/,/g, "")) : null;
}

function Banner({ kind = "info", children }) {
  const styles = {
    info: {
      background: "#eef5ff",
      border: "1px solid #bcd2ff",
      color: "#123b7a",
    },
    warn: {
      background: "#fff7d6",
      border: "1px solid #f1d488",
      color: "#7a5b00",
    },
    error: {
      background: "#ffecec",
      border: "1px solid #f2b7b7",
      color: "#7a0b0b",
    },
    success: {
      background: "#e8f6ec",
      border: "1px solid #bfe3ca",
      color: "#155b2a",
    },
  };
  return (
    <div
      role="alert"
      style={{
        ...styles[kind],
        padding: "10px 12px",
        borderRadius: 8,
        margin: "12px 0",
      }}
    >
      {children}
    </div>
  );
}

const cleanValue = (value) => String(value ?? "").trim();
const uniqueBy = (items, keyFn) => {
  const seen = new Set();
  const out = [];
  (items || []).forEach((item) => {
    const key = keyFn(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
};
const materialRowsFor = (rows, vendor, materialName, sku = "") => {
  const vendorValue = cleanValue(vendor);
  const mat = cleanValue(materialName);
  const skuValue = cleanValue(sku);
  return (rows || []).filter((row) => {
    const okVendor = cleanValue(row.vendor) === vendorValue;
    const okMaterial = cleanValue(row.material) === mat;
    const okSku = skuValue ? cleanValue(row.sku) === skuValue : true;
    return okVendor && okMaterial && okSku;
  });
};
const materialIdentityCandidates = (rows, vendor, materialName, sku) => {
  const matches = materialRowsFor(rows, vendor, materialName, sku);
  const byId = new Map();
  const legacy = [];
  matches.forEach((row) => {
    const mid = cleanValue(row.material_id);
    if (mid) {
      if (!byId.has(mid)) byId.set(mid, row);
    } else {
      legacy.push(row);
    }
  });
  return { matches, distinctIds: Array.from(byId.keys()), byId, legacy };
};

const resolveMaterialCandidate = (rows, vendor, materialName, sku) => {
  const candidates = materialIdentityCandidates(
    rows,
    vendor,
    materialName,
    sku,
  );
  return candidates.distinctIds.length === 1
    ? candidates.byId.get(candidates.distinctIds[0])
    : null;
};
const extractionLotRowId = (row) =>
  [
    cleanValue(row?.vendor),
    cleanValue(row?.material_id) ||
      `legacy:${cleanValue(row?.material)}:${cleanValue(row?.sku)}`,
    cleanValue(row?.lot_no),
  ].join("::");

function isLocalDateInRange(value, range) {
  const raw = String(value || "").slice(0, 10);
  if (!raw) return true;
  if (range?.date_from && raw < range.date_from) return false;
  if (range?.date_to && raw > range.date_to) return false;
  return true;
}

function violatesThreshold(count, rule) {
  if (
    !rule ||
    !rule.criteria ||
    String(rule.criteria).trim().toLowerCase() === "nil"
  )
    return false;
  const n = Number(count ?? 0);
  const need = Number(rule.count ?? 0);
  const crit = String(rule.criteria || "")
    .trim()
    .toLowerCase();
  if (crit === "max") return n > need;
  if (crit === "min") return n < need;
  if (crit === "equals") return n !== need;
  return false;
}

//new code:

function normalizeThreshold(thr) {
  // Accepts {Y:{criteria,count}, N:{...}, NA:{...}} in any case
  if (!thr || typeof thr !== "object") return null;
  const out = {};
  for (const k of Object.keys(thr)) {
    const K = String(k).toUpperCase();
    const r = thr[k] || {};
    out[K] = {
      criteria: String(r.criteria || "")
        .trim()
        .toLowerCase(), // 'min' | 'max' | 'equals' | 'nil'
      count: Number(r.count ?? 0),
    };
  }
  return out;
}

function normalizeCounts(counts) {
  // Accepts counts with any casing: y/Y, n/N, na/NA
  counts = counts || {};
  return {
    Y: Number(counts.Y ?? counts.y ?? 0),
    N: Number(counts.N ?? counts.n ?? 0),
    NA: Number(counts.NA ?? counts.na ?? 0),
  };
}

function isBadByRule(valueNum, rule) {
  if (!rule || rule.criteria === "nil") return false;
  const n = Number(valueNum ?? 0);
  const need = Number(rule.count ?? 0);
  if (rule.criteria === "max") return n > need;
  if (rule.criteria === "min") return n < need;
  if (rule.criteria === "equals") return n !== need;
  return false;
}

function badFor(code, values, thr) {
  const T = normalizeThreshold(thr);
  const C = normalizeCounts(values?.counts);
  if (!T) return false;
  const rule = T[String(code).toUpperCase()];
  return isBadByRule(C[String(code).toUpperCase()], rule);
}

function renderHeaderValueCell(values, thr, label) {
  const header = values?.header || {};
  const rawCounts = values?.counts || {};
  const T = normalizeThreshold(thr) || {};

  const norm = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/:\s*$/, "")
      .replace(/\s+/g, " ");

  // Try header fuzzy fetch only if a count is missing
  const findHeaderNum = (needle) => {
    const nneedle = norm(needle);
    for (const k of Object.keys(header || {})) {
      if (norm(k) === nneedle) {
        return numberish(header[k]);
      }
    }
    return null;
  };

  // Prefer counts.* if present (not null/undefined); otherwise fallback to header
  const hasY = rawCounts.Y != null || rawCounts.y != null;
  const hasN = rawCounts.N != null || rawCounts.n != null;
  const hasNA = rawCounts.NA != null || rawCounts.na != null;

  const yVal = hasY
    ? Number(rawCounts.Y ?? rawCounts.y)
    : findHeaderNum("enter yes");
  const nVal = hasN
    ? Number(rawCounts.N ?? rawCounts.n)
    : findHeaderNum("enter no");
  const naVal = hasNA
    ? Number(rawCounts.NA ?? rawCounts.na)
    : findHeaderNum("enter n/a");

  const flagY = isBadByRule(yVal, T.Y);
  const flagN = isBadByRule(nVal, T.N);
  const flagNA = isBadByRule(naVal, T.NA);

  const dangerPill = {
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 600,
    borderRadius: 6,
    padding: "1px 6px",
    display: "inline-block",
  };

  const key = norm(label);
  const isCombo =
    key === "enter y/n/na" ||
    key === "enter y / n / na" ||
    key === "enter y n na";

  if (isCombo) {
    return (
      <span>
        Y:&nbsp;<span style={flagY ? dangerPill : {}}>{yVal ?? ""}</span>
        &nbsp;&nbsp;N:&nbsp;
        <span style={flagN ? dangerPill : {}}>{nVal ?? ""}</span>
        &nbsp;&nbsp;NA:&nbsp;
        <span style={flagNA ? dangerPill : {}}>{naVal ?? ""}</span>
      </span>
    );
  }

  if (key.includes("enter yes"))
    return <span style={flagY ? dangerPill : {}}>{yVal ?? ""}</span>;
  if (key.includes("enter no"))
    return <span style={flagN ? dangerPill : {}}>{nVal ?? ""}</span>;
  if (key.includes("enter n/a") || key.includes("enter na"))
    return <span style={flagNA ? dangerPill : {}}>{naVal ?? ""}</span>;

  const rawValue = isChecklistMonthLabel(label)
    ? getChecklistMonthHeaderValue(header, label)
    : (header[label] ?? header[label?.replace(/:\s*$/, " :")] ?? "");
  const displayValue = isChecklistMonthLabel(label)
    ? formatChecklistMonth(rawValue)
    : rawValue;

  // Non Y/N/NA header cells — unchanged except checklist month display formatting.
  return (
    <span>{String(displayValue)}</span>
  );
}

function mrpDisplayMeta(check, label) {
  const cfg = check?.mrp_config || {};
  if (!isMrpHeaderLabel(label)) return { style: {}, info: "" };
  return {
    info: cfg.has_std_mrp ? cfg.info_text || "" : "",
    style:
      cfg.has_threshold && cfg.violates_threshold
        ? {
            background: "rgba(254, 226, 226, 0.92)",
            color: "#991b1b",
            fontWeight: 700,
            borderRadius: 10,
            padding: "2px 8px",
            display: "inline-block",
          }
        : {},
  };
}

function headerCountStyle(values, thr, label) {
  if (!values || !thr || !label) return {};

  const header = values.header || {};
  const counts = values.counts || {};

  // --- normalize threshold ---
  const T = {};
  for (const k of ["Y", "N", "NA"]) {
    const r = thr[k] || thr[k.toLowerCase()] || thr[k.toUpperCase()] || {};
    T[k] = {
      criteria: String(r.criteria || "")
        .trim()
        .toLowerCase(),
      count: Number(r.count ?? 0),
    };
  }
  const isBad = (n, rule) => {
    if (!rule || rule.criteria === "nil") return false;
    const v = Number(n ?? 0),
      need = Number(rule.count ?? 0);
    if (rule.criteria === "max") return v > need;
    if (rule.criteria === "min") return v < need;
    if (rule.criteria === "equals") return v !== need;
    return false;
  };

  // same normalization used in renderHeaderValueCell
  const norm = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/:\s*$/, "")
      .replace(/\s+/g, " ");
  const key = norm(label);

  // counts-first: prefer counts if present; otherwise fallback to header lookup
  const hasY = counts.Y != null || counts.y != null;
  const hasN = counts.N != null || counts.n != null;
  const hasNA = counts.NA != null || counts.na != null;

  const readHeaderNum = (needle) => {
    const target = norm(needle);
    for (const k of Object.keys(header || {})) {
      if (norm(k) === target) return numberish(header[k]);
    }
    return null;
  };

  const yVal = hasY ? Number(counts.Y ?? counts.y) : readHeaderNum("enter yes");
  const nVal = hasN ? Number(counts.N ?? counts.n) : readHeaderNum("enter no");
  const naVal = hasNA
    ? Number(counts.NA ?? counts.na)
    : readHeaderNum("enter n/a");

  const badY = isBad(yVal, T.Y);
  const badN = isBad(nVal, T.N);
  const badNA = isBad(naVal, T.NA);

  const cellDanger = {
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 600,
  };

  // handle combined row too
  const isCombo =
    key === "enter y/n/na" ||
    key === "enter y / n / na" ||
    key === "enter y n na";

  if (isCombo) return badY || badN || badNA ? cellDanger : {};

  if (key.includes("enter yes")) return badY ? cellDanger : {};
  if (key.includes("enter no")) return badN ? cellDanger : {};
  if (key.includes("enter n/a") || key.includes("enter na"))
    return badNA ? cellDanger : {};

  return {};
}

function headerDisplayValue(values, label) {
  if (!values) return "";
  const header = values.header || {};
  const counts = values.counts || {};
  const key = String(label).trim().toLowerCase().replace(/\s+/g, " ");
  if (key === "enter yes:") return counts.Y ?? counts.y ?? header[label] ?? "";
  if (key === "enter no:") return counts.N ?? counts.n ?? header[label] ?? "";
  if (key === "enter n/a:")
    return counts.NA ?? counts.na ?? header[label] ?? "";
  // any other header field
  return header[label] ?? header[label?.replace(/:\s*$/, " :")] ?? "";
}

export default function CoaExtractionsPage() {
  const { user } = useAuth();
  const { current: currentOrg } = useOrg();
  const { t, tp } = useTerminology();
  const [extractionLots, setExtractionLots] = React.useState([]);
  const [vendor, setVendor] = React.useState("");
  const [material, setMaterial] = React.useState("");
  const [sku, setSku] = React.useState("");
  const [materialId, setMaterialId] = React.useState("");
  const [lot, setLot] = React.useState("");
  const [pdfUrl, setPdfUrl] = React.useState("");
  const [specs, setSpecs] = React.useState([]);
  const [results, setResults] = React.useState([]);
  const [conf, setConf] = React.useState([]);
  const [crit, setCrit] = React.useState({ op: "<", val: 70 });
  const [preview, setPreview] = React.useState(null);
  const [includeOogt, setIncludeOogt] = React.useState(false);
  const [banner, setBanner] = React.useState(null);
  const [cacheMeta, setCacheMeta] = React.useState(null);

  const [check, setCheck] = React.useState(null);
  const [checkUrl, setCheckUrl] = React.useState("");

  const [pinOpen, setPinOpen] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState(null);
  const [invalidateModal, setInvalidateModal] = React.useState({
    open: false,
    comments: "",
  });
  const [thrCfg, setThrCfg] = React.useState(null);
  const [settings, setSettings] = React.useState(null);
  const [lotTableOpen, setLotTableOpen] = React.useState(true);
  const [lotStatusFilter, setLotStatusFilter] = React.useState("All");
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });
  const checklistEnabled = !!settings?.checklist_enabled;
  
  const backendErrorText = React.useCallback((err, fallback) => {
    return (
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      fallback
    );
  }, []);

  const vendors = [...new Set(extractionLots.map((i) => i.vendor))];
  const vendorRows = React.useMemo(
    () =>
      extractionLots.filter((i) => cleanValue(i.vendor) === cleanValue(vendor)),
    [extractionLots, vendor],
  );
  const materialNameOptions = React.useMemo(
    () =>
      uniqueBy(vendorRows, (row) => cleanValue(row.material))
        .map((row) => cleanValue(row.material))
        .filter(Boolean),
    [vendorRows],
  );
  const skuOptions = React.useMemo(
    () =>
      uniqueBy(materialRowsFor(extractionLots, vendor, material), (row) =>
        cleanValue(row.sku),
      )
        .map((row) => cleanValue(row.sku))
        .filter(Boolean),
    [extractionLots, vendor, material],
  );
  const materialIdentityState = React.useMemo(
    () =>
      material && sku
        ? materialIdentityCandidates(extractionLots, vendor, material, sku)
        : { matches: [], distinctIds: [], byId: new Map(), legacy: [] },
    [extractionLots, vendor, material, sku],
  );
  const hasMaterialAmbiguity =
    !materialId && materialIdentityState.distinctIds.length > 1;
  const materialResolutionMessage =
    material && sku && hasMaterialAmbiguity
      ? `Multiple ${tp("material").toLowerCase()} match this ${t("material")} + SKU. Please review the ${t("material").toLowerCase()} library before continuing.`
      : "";
  const lotOptions = React.useMemo(() => {
    if (hasMaterialAmbiguity) return [];
    const seen = new Set();
    const out = [];
    materialIdentityState.matches
      .filter(
        (i) =>
          !materialId || String(i.material_id || "") === String(materialId),
      )
      .forEach((i) => {
        const lotNo = cleanValue(i.lot_no);
        if (!lotNo || seen.has(lotNo)) return;
        seen.add(lotNo);
        out.push(i);
      });
    return out;
  }, [materialIdentityState, materialId, hasMaterialAmbiguity]);
  const selectedExtractionRowId = React.useMemo(() => {
    if (!vendor || !sku || !lot) return "";
    return extractionLotRowId({
      vendor,
      material,
      sku,
      material_id: materialId,
      lot_no: lot,
    });
  }, [vendor, material, sku, materialId, lot]);
  const selectedLotRow = React.useMemo(
    () =>
      extractionLots.find(
        (row) => extractionLotRowId(row) === selectedExtractionRowId,
      ) || null,
    [extractionLots, selectedExtractionRowId],
  );
  const selectedLotCanOpen = !!selectedLotRow?.can_open;
  const selectedLotStatus = cleanValue(selectedLotRow?.status);
  const selectedLotStatusKey = selectedLotStatus.toLowerCase();
  const selectedConfirmedFailed =
    selectedLotStatusKey === "failed" &&
    selectedLotRow?.failure_kind === "confirmed";
  const canDirectInvalidate =
    selectedLotCanOpen &&
    (selectedConfirmedFailed ||
      (selectedLotStatusKey === "initiated" && !!cacheMeta));
  const requestParams = React.useMemo(
    () => ({
      vendor,
      sku,
      lot_no: lot,
      ...(materialId ? { material_id: materialId } : {}),
    }),
    [vendor, sku, lot, materialId],
  );

  React.useEffect(() => {
    setPdfUrl("");
    setSpecs([]);
    setResults([]);
    setConf([]);
    setPreview(null);
    setCacheMeta(null);
    if (!vendor || !sku || !lot || !selectedLotCanOpen) return;
    const vendorQS = new URLSearchParams(requestParams).toString();
    const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
    setPdfUrl(
      withOrgQS(`${API_BASE}/user/get_processed_pdf?${vendorQS}`, currentOrg),
    );
    api
      .get("/user/get_processed_cache", { params: requestParams })
      .then((r) => {
        const s = r.data.specs || [];
        const res = r.data.results || [];
        const c = r.data.confidences || [];
        const meta = r.data.meta || null;
        setCacheMeta(meta);
        setSpecs(s);
        if (
          String(meta?._extraction_status || "").toLowerCase() ===
          "failed"
        ) {
          setResults([]);
          setConf([]);
          setPreview(null);
          setLotTableOpen(false);
          return;
        }
        const adj = s.map((row, idx) => {
          const cautionYes =
            String(row?.Caution || "")
              .trim()
              .toLowerCase() === "yes";
          return cautionYes ? 0.5 : (c[idx] ?? 0);
        });
        setResults(res.slice());
        setConf(adj);
        setLotTableOpen(false);
      })
      .catch((err) => {
        setBanner({
          kind: "error",
          text: backendErrorText(err, "Could not load extracted results."),
        });
      });
    api.get("/orgs/settings").then((r) => {
      const s = r.data || {};
      setSettings(s);
      setCrit({
        op: s.crit_op || "<",
        val: parseInt(s.crit_threshold ?? 70, 10),
      });
    });
  }, [
    vendor,
    sku,
    lot,
    materialId,
    requestParams,
    currentOrg,
    backendErrorText,
    selectedLotCanOpen,
  ]);

  const refreshExtractionLots = React.useCallback(() => {
    api
      .get("/user/extraction_lots", {
        params: { statuses: "Queued,Initiated,Failed" },
      })
      .then((r) => setExtractionLots(r.data.items || []))
      .catch(() => setExtractionLots([]));
  }, []);

  React.useEffect(() => {
    refreshExtractionLots();
  }, [refreshExtractionLots]);

  React.useEffect(() => {
    const hasQueued = extractionLots.some(
      (row) => cleanValue(row.status).toLowerCase() === "queued",
    );
    if (!hasQueued) return undefined;
    const timer = setInterval(refreshExtractionLots, 10000);
    return () => clearInterval(timer);
  }, [extractionLots, refreshExtractionLots]);

  React.useEffect(() => {
    setCheck(null);
    setCheckUrl("");
    if (!vendor || !sku || !lot || !selectedLotCanOpen) return;
    if (!checklistEnabled) {
      return;
    }
    api
      .get("/checklists/get", { params: requestParams })
      .then(async (r) => {
        setCheck(r.data || null);

        //setCheckUrl(`${process.env.REACT_APP_API_BASE}/user/get_checklist_pdf?vendor=${encodeURIComponent(vendor)}&sku=${encodeURIComponent(sku)}&lot_no=${encodeURIComponent(lot)}`);
        const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(
          /\/$/,
          "",
        );
        const qs = new URLSearchParams(requestParams).toString();
        setCheckUrl(
          withOrgQS(`${API_BASE}/user/get_checklist_pdf?${qs}`, currentOrg),
        );
        const mtype = r.data?.type || "Others";
        const thrType = r.data?.threshold_type || mtype;
        const inlineThr = r.data?.threshold || null;
        if (inlineThr) {
          setThrCfg(inlineThr);
        } else {
          try {
            /*const tr = await api.get("/checklists/threshold", {
        params: { material_type: mtype },
      });
      */
            const tr = await api.get("/checklists/threshold", {
              params: { material_type: thrType },
            });
            const fetchedThr = tr.data?.threshold || null;
            setThrCfg(fetchedThr);
          } catch (e) {
            setThrCfg(null);
            console.warn("threshold fetch failed for", mtype, e);
          }
          //if (r.data?.threshold) {
          //setThrCfg(r.data.threshold);
          //} else {
          //try {
          //const tr = await api.get("/checklists/threshold", { params: { material_type: mtype }});
          //setThrCfg(tr.data?.threshold || null);
          //} catch { setThrCfg(null); }
        }
      })
      .catch((err) => {
        console.error("❌ /checklists/get failed", err);
        setThrCfg(null);
      });
  }, [
    vendor,
    sku,
    lot,
    materialId,
    requestParams,
    checklistEnabled,
    currentOrg,
    selectedLotCanOpen,
  ]);

  React.useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const toPairs = (headerTemplate, values) => {
    const pairs = headerTemplate?.pairs;
    if (pairs && pairs.length) return pairs;
    const h = values?.header || {};
    const keys = Object.keys(h)
      .filter((k) => String(k).trim())
      .sort((a, b) => a.localeCompare(b));
    const out = [];
    for (let i = 0; i < keys.length; i += 2) out.push(keys.slice(i, i + 2));
    return out;
  };
  /*
  const submitPreview = async () => {
    const edited_results_json = JSON.stringify(results);
    const res = await postForm("/user/preview_checks", {
      user_id: user.uid, vendor, sku, lot_no: lot,
      edited_results_json,
      include_oogt: includeOogt ? "1" : "0"
    });
    setPreview(res.data);
    return(
       {Array.isArray(preview?.flags) && preview.flags.length>0 && (
           <div className="section" style={{borderColor:"#f3b3b3"}}>
             <b>Checklist Flags</b>
             <ul>{preview.flags.map((f,i)=><li key={i} style={{color:"#b31212"}}>{f}</li>)}</ul>
           </div>
        )}
    );
  };
*/
  const submitPreview = async () => {
    try {
      const edited_results_json = JSON.stringify(results);
      const res = await postForm("/user/preview_checks", {
        user_id: user.uid,
        vendor,
        sku,
        material_id: materialId,
        lot_no: lot,
        edited_results_json,
        include_oogt: includeOogt ? "1" : "0",
      });
      setPreview(res.data);
    } catch (err) {
      setBanner({
        kind: "error",
        text: backendErrorText(err, "Could not run preview. Please retry."),
      });
    }
  };
  const confirmSave = async () => {
    if (hasMaterialAmbiguity) {
      setBanner({
        kind: "error",
        text: `Multiple ${tp("material").toLowerCase()} match this ${t("material")} + SKU. Please review the ${t("material").toLowerCase()} library before saving.`,
      });
      return;
    }
    try {
      const edited_results_json = JSON.stringify(results);
      const res = await postForm("/user/submit_coa", {
        user_id: user.uid,
        vendor,
        sku,
        material_id: materialId,
        lot_no: lot,
        edited_results_json,
        include_oogt: includeOogt ? "1" : "0",
      });
      setBanner({
        kind: "success",
        text: `Saved. Final decision: ${res.data.decision}`,
      });
      setPreview(null);
      setPdfUrl("");
      setSpecs([]);
      setResults([]);
      setConf([]);
      setLot("");
      setLotTableOpen(true);
      refreshExtractionLots();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setBanner({ kind: "warn", text: `${t("lot")} already queued` });
      } else {
        setBanner({
          kind: "error",
          text: backendErrorText(
            err,
            "Something went wrong while saving. Please retry.",
          ),
        });
      }
    }
  };

  const sendForReview = async () => {
    if (hasMaterialAmbiguity) {
      setBanner({
        kind: "error",
        text: `Multiple ${tp("material").toLowerCase()} match this ${t("material")} + SKU. Please review the ${t("material").toLowerCase()} library before sending for review.`,
      });
      return;
    }
    try {
      const edited_results_json = JSON.stringify(results || []);
      await postForm("/user/send_for_review", {
        user_id: user.uid,
        vendor,
        sku,
        material_id: materialId,
        lot_no: lot,
        edited_results_json,
      });
      setBanner({ kind: "success", text: "Sent for manager review." });
      setPreview(null);
      setPdfUrl("");
      setSpecs([]);
      setResults([]);
      setConf([]);
      setLot("");
      setLotTableOpen(true);
      refreshExtractionLots();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setBanner({ kind: "warn", text: `${t("lot")} already queued` });
      } else {
        setBanner({
          kind: "error",
          text: backendErrorText(
            err,
            "Could not send for review. Please retry.",
          ),
        });
      }
    }
  };

  const invalidateLot = async (comments = "") => {
    if (hasMaterialAmbiguity) {
      setBanner({
        kind: "error",
        text: `Multiple ${tp("material").toLowerCase()} match this ${t("material")} + SKU. Please review the ${t("material").toLowerCase()} library before invalidating.`,
      });
      return;
    }
    if (!canDirectInvalidate) {
      setBanner({
        kind: "warn",
        text: `Only ready Initiated or confirmed Failed ${tp("lot").toLowerCase()} can be sent for re-upload.`,
      });
      return;
    }
    try {
      await postForm("/user/invalidate_lot", {
        user_id: user.uid,
        vendor,
        sku,
        material_id: materialId,
        lot_no: lot,
        comments: comments || "Sent back to vendor for re-upload.",
      });
      setBanner({
        kind: "success",
        text: `${t("lot")} invalidated and sent for re-upload.`,
      });
      setPreview(null);
      setPdfUrl("");
      setSpecs([]);
      setResults([]);
      setConf([]);
      setCacheMeta(null);
      setCheck(null);
      setCheckUrl("");
      setLot("");
      setLotTableOpen(true);
      refreshExtractionLots();
    } catch (err) {
      setBanner({
        kind: "error",
        text: backendErrorText(
          err,
          `Could not invalidate this ${t("lot").toLowerCase()}. Please retry.`,
        ),
      });
    }
  };

  const openInvalidateModal = () => {
    if (hasMaterialAmbiguity) {
      setBanner({
        kind: "error",
        text: `Multiple ${tp("material").toLowerCase()} match this ${t("material")} + SKU. Please review the ${t("material").toLowerCase()} library before invalidating.`,
      });
      return;
    }
    if (!canDirectInvalidate) {
      setBanner({
        kind: "warn",
        text: `Only ready Initiated or confirmed Failed ${tp("lot").toLowerCase()} can be sent for re-upload.`,
      });
      return;
    }
    setInvalidateModal({ open: true, comments: "" });
  };

  const continueInvalidateWithPin = () => {
    const comments = invalidateModal.comments || "";
    setInvalidateModal({ open: false, comments: "" });
    setPendingAction(() => () => invalidateLot(comments));
    setPinOpen(true);
  };

  const clearLoadedLotState = React.useCallback(() => {
    setPdfUrl("");
    setSpecs([]);
    setResults([]);
    setConf([]);
    setPreview(null);
    setCacheMeta(null);
    setCheck(null);
    setCheckUrl("");
    setThrCfg(null);
  }, []);

  const selectExtractionLotRow = React.useCallback(
    (row) => {
      const nextVendor = cleanValue(row?.vendor);
      const nextMaterial = cleanValue(row?.material);
      const nextSku = cleanValue(row?.sku);
      const nextMaterialId = cleanValue(row?.material_id);
      const nextLot = cleanValue(row?.lot_no);
      clearLoadedLotState();
      setVendor(nextVendor);
      setMaterial(nextMaterial);
      setSku(nextSku);
      setMaterialId(nextMaterialId);
      setLot(nextLot);
      const statusKey = cleanValue(row?.status).toLowerCase();
      if (!row?.can_open) {
        setBanner({
          kind: statusKey === "queued" ? "info" : "warn",
          text:
            row?.processing_label ||
            (statusKey === "queued"
              ? `Extraction in progress. This ${t("lot").toLowerCase()} will be available once processing finishes.`
              : `This ${t("lot").toLowerCase()} is not ready to open yet.`),
        });
        return;
      }
      setBanner(null);
    },
    [clearLoadedLotState, t],
  );

  const extractionLotColumns = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      { key: "lot_no", label: t("lot") },
      { key: "status", label: "Status", render: (row) => row.status || "—" },
      {
        key: "last_action_time",
        label: "Last Action",
        value: (row) => row.last_action_time || row.upload_time || "",
        render: (row) =>
          formatDateTime(row.last_action_time || row.upload_time),
      },
    ],
    [t],
  );

  const extractionLotSortOptions = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      { key: "lot_no", label: t("lot") },
      { key: "status", label: "Status" },
      {
        key: "last_action_time",
        label: "Last Action",
        value: (row) => row.last_action_time || row.upload_time || "",
      },
    ],
    [t],
  );

  const filteredExtractionLots = React.useMemo(() => {
    return extractionLots.filter(
      (row) =>
        (lotStatusFilter === "All" ||
          cleanValue(row.status).toLowerCase() ===
            lotStatusFilter.toLowerCase()) &&
        isLocalDateInRange(
          row.last_action_time || row.upload_time,
          dateRange,
        ),
    );
  }, [extractionLots, lotStatusFilter, dateRange]);

  const extractionLotEmptyText =
    lotStatusFilter === "All"
      ? `No queued, initiated, or confirmed Failed COA ${tp("lot").toLowerCase()} found.`
      : `No ${lotStatusFilter} ${tp("lot").toLowerCase()} found.`;

  const extractionToolbarControls = (
    <>
      <label>
        <span>Status</span>
        <select
          value={lotStatusFilter}
          onChange={(event) => setLotStatusFilter(event.target.value)}
        >
          <option value="All">All</option>
          <option value="Queued">Queued</option>
          <option value="Initiated">Initiated</option>
          <option value="Failed">Failed</option>
        </select>
      </label>
      <DateRangePicker
        value={dateDraft}
        onChange={setDateDraft}
        onApply={() => setDateRange({ ...dateDraft })}
        onClear={(empty) => setDateRange(empty)}
        label="Last action range"
      />
    </>
  );

  return (
    <div className="section">
      <h2>COA Extractions</h2>
      {banner ? <Banner kind={banner.kind}>{banner.text}</Banner> : null}
      <details
        className="selection-expander"
        open={lotTableOpen}
        onToggle={(event) => setLotTableOpen(event.currentTarget.open)}
      >
        <summary>Select COA {t("lot").toLowerCase()}</summary>
        <div className="helper-text" style={{ marginBottom: 10 }}>
          {`Queued ${tp("lot").toLowerCase()} remain visible while extraction runs. Open an Initiated or confirmed Failed row to review the COA.`}
        </div>
        <LotSelectionTable
          rows={filteredExtractionLots}
          columns={extractionLotColumns}
          sortOptions={extractionLotSortOptions}
          toolbarExtras={extractionToolbarControls}
          selected={selectedExtractionRowId}
          onRowClick={selectExtractionLotRow}
          onDisabledRowClick={selectExtractionLotRow}
          getRowId={extractionLotRowId}
          disabledRow={(row) => !row?.can_open}
          searchPlaceholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, or status`}
          emptyText={extractionLotEmptyText}
          pageSize={10}
          className="coa-extractions-lot-table"
        />
      </details>

      {selectedLotRow ? (
        <div className="selected-lot-summary" style={{ marginTop: 12 }}>
          <div>
            <b>Selected:</b> {vendor || "—"} · {material || "—"} · {sku || "—"}{" "}
            · {t("lot")} {lot || "—"}
          </div>
        </div>
      ) : (
        <div className="helper-text" style={{ marginTop: 12 }}>
          Select a COA {t("lot").toLowerCase()} from the table to review it.
        </div>
      )}

      {selectedLotRow && !selectedLotCanOpen && (
        <Banner kind={selectedLotStatusKey === "queued" ? "info" : "warn"}>
          <div>
            <b>
              {selectedLotStatusKey === "queued"
                ? "Extraction in progress."
                : `This ${t("lot").toLowerCase()} is not ready to open.`}
            </b>
          </div>
          <div style={{ marginTop: 6 }}>
            {selectedLotRow.processing_label ||
              "The processed cache is not available yet."}
          </div>
        </Banner>
      )}

      {pdfUrl && (
        <>
          {selectedConfirmedFailed ? (
            <Banner kind="warn">
              This COA could not be processed. Check that the uploaded COA matches the selected material. You can invalidate it for re-upload or send it to a Manager for review.
            </Banner>
          ) : null}
          <details open>
            <summary>
              <b>COA Extraction</b>
            </summary>
            <div className="two-pane" style={{ marginTop: 16 }}>
              <div className="pane pdf-pane">
                <PdfViewer url={pdfUrl} />
              </div>

              <div className="pane table-pane">
                  <div className="table--card">
                    <div className="table-scroll">
                      <table className="table table--tight">
                        <colgroup>
                          <col style={{ width: "40%" }} />
                          <col />
                          <col style={{ width: "80px" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>{selectedConfirmedFailed ? "Result" : "Result (editable)"}</th>
                            <th>Conf</th>
                          </tr>
                        </thead>
                        <tbody>
                          {specs.map((s, i) => {
                            const cval = Number(conf[i] ?? 0);
                            //const low = !Number.isNaN(cval) && cval < 0.97;
                            const isLow = (cval) => {
                              const pct = Math.round(100 * (cval || 0));
                              switch (crit.op) {
                                case ">":
                                  return pct > crit.val;
                                case "=":
                                  return pct === crit.val;
                                case "<":
                                default:
                                  return pct < crit.val;
                              }
                            };
                            const dangerStyle = isLow(cval)
                              ? {
                                  background: "#fee2e2",
                                  color: "#991b1b",
                                  fontWeight: 600,
                                }
                              : {};
                            return (
                              <tr key={i}>
                                <td>{s.Parameter}</td>
                                <td style={dangerStyle}>
                                  <input
                                    value={results[i] ?? ""}
                                    disabled={selectedConfirmedFailed}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setResults((r) => {
                                        const c = r.slice();
                                        c[i] = v;
                                        return c;
                                      });
                                    }}
                                    style={dangerStyle}
                                  />
                                </td>
                                {/*<td>{cval.toFixed(2)}</td>*/}
                                <td>
                                  {selectedConfirmedFailed ? "—" : Number.isFinite(cval)
                                    ? cval.toFixed(2)
                                    : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
              </div>
            </div>
          </details>

          {/*{check && ( */}
          {checklistEnabled && check && (
            <details style={{ marginTop: 16 }}>
              <summary>
                <b>Checklist</b>
              </summary>
              <div className="two-pane" style={{ marginTop: 12 }}>
                <div className="pane pdf-pane">
                  <PdfViewer url={checkUrl} />
                </div>
                <div className="pane table-pane">
                  <div className="table--card">
                    <div className="table-scroll">
                      <h4 style={{ margin: "8px 10px" }}>
                        Header (vendor filled)
                      </h4>
                      <table className="table table--tight">
                        <tbody>
                          {toPairs(check.header_template, check.values).map(
                            (pair, i) => (
                              <tr key={i}>
                                {pair.map((label, j) => (
                                  <React.Fragment key={j}>
                                    <td style={{ width: 160 }}>
                                      <b>{label}</b>
                                      {mrpDisplayMeta(check, label).info ? (
                                        <InlineFieldInfo
                                          text={
                                            mrpDisplayMeta(check, label).info
                                          }
                                          label={`${label} MRP information`}
                                        />
                                      ) : null}
                                    </td>
                                    {/*<td>{check.values?.header?.[label] || ""}</td>*/}

                                    {/*<td style={headerCountStyle(check.values?.header, thrCfg, label)}>
                                  {check.values?.header?.[label] || ""}
                                </td>*/}

                                    {/* <td style={headerCountStyle(check.values, thrCfg, label)}>
                                     {check.values?.header?.[label] || ""}
                                   </td>*/}

                                    <td
                                      style={{
                                        ...headerCountStyle(
                                          check.values,
                                          thrCfg,
                                          label,
                                        ),
                                        ...mrpDisplayMeta(check, label).style,
                                      }}
                                    >
                                      {renderHeaderValueCell(
                                        check.values,
                                        thrCfg,
                                        label,
                                      )}
                                    </td>
                                  </React.Fragment>
                                ))}
                                {pair.length === 1 && (
                                  <>
                                    <td></td>
                                    <td></td>
                                  </>
                                )}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding: "8px 10px" }}>
                      <div>
                        <b>Question:</b> {check.question?.text}{" "}
                        <small>({check.question?.code})</small>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <b>Answer:</b>{" "}
                        <span
                          style={{
                            padding: "2px 6px",
                            borderRadius: 6,
                            ...(String(
                              check.values?.answer || "N/A",
                            ).toUpperCase() !== "N/A"
                              ? {
                                  background: "#fee2e2",
                                  color: "#991b1b",
                                  fontWeight: 600,
                                }
                              : {}),
                          }}
                        >
                          {String(check.values?.answer || "N/A")}
                        </span>
                        {String(check.values?.answer || "").toUpperCase() ===
                          "Y" && (
                          <span style={{ marginLeft: 10 }}>
                            <b>Deviation No.:</b>{" "}
                            {check.values?.deviation_no || ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          )}
          {/* )} */}

          {(!!cacheMeta || selectedConfirmedFailed) && (
            <div className="pane-actions">
              {!selectedConfirmedFailed ? (
                <button className="primary" onClick={submitPreview}>
                  Submit (Preview checks)
                </button>
              ) : null}
              <button
                className="ghost"
                onClick={() => {
                  setPendingAction(() => sendForReview);
                  setPinOpen(true);
                }}
              >
                Send for Review
              </button>
              {canDirectInvalidate ? (
                <button className="ghost danger" onClick={openInvalidateModal}>
                  Invalidate / Send for Re-upload
                </button>
              ) : null}
            </div>
          )}

          {!selectedConfirmedFailed && preview && (
            <div className="section" style={{ marginTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>Submission checks</h3>
              {Array.isArray(preview?.flags) && preview.flags.length > 0 && (
                <div className="section" style={{ borderColor: "#f3b3b3" }}>
                  <b>Checklist Flags</b>
                  <ul>
                    {preview.flags.map((f, i) => (
                      <li key={i} style={{ color: "#b31212" }}>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {preview?.force_review_reason === "cache_blank_results" && (
                <Banner kind="warn">
                  <div>
                    There are empty results present in this COA.
                    Confirm and Save will send this for manager review.
                  </div>
                  {Array.isArray(preview?.cache_blank_parameters) &&
                    preview.cache_blank_parameters.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <b>Blank parameter(s):</b>{" "}
                        {preview.cache_blank_parameters.slice(0, 5).join(", ")}
                        {preview.cache_blank_parameters.length > 5
                          ? ` +${preview.cache_blank_parameters.length - 5} more`
                          : ""}
                      </div>
                    )}
                </Banner>
              )}
              <div style={{ marginBottom: 8 }}>
                <b>Suggested decision:</b> {preview.decision || "Pending"}
              </div>

              <div className="hstack" style={{ marginTop: 8 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={includeOogt}
                    onChange={(e) => setIncludeOogt(e.target.checked)}
                  />
                  &nbsp;Include OOGT
                </label>
                <button
                  className="ghost"
                  style={{ marginLeft: 8 }}
                  onClick={submitPreview}
                >
                  Re-run Preview
                </button>
              </div>
              <div
                className="grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                }}
              >
                <div className="table--card">
                  <h4 style={{ margin: "8px 10px" }}>OOS</h4>
                  <table className="table table--dense">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Current</th>
                        <th>LL</th>
                        <th>UL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.oos || []).map((r, i) => (
                        <tr key={i}>
                          <td>{r.Parameter}</td>
                          <td>{r.Current}</td>
                          <td>{r.Lower}</td>
                          <td>{r.Upper}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="table--card">
                  <h4 style={{ margin: "8px 10px" }}>OOT</h4>
                  <table className="table table--dense">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>Current</th>
                        <th>95% Lower</th>
                        <th>95% Upper</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.oot || []).map((r, i) => {
                        const f = (v) =>
                          v == null ? "" : Number(v).toFixed(3);
                        return (
                          <tr key={i}>
                            <td>{r.Parameter}</td>
                            <td>{r.Current}</td>
                            <td>{f(r["95 lower"])}</td>
                            <td>{f(r["95 upper"])}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="table--card" style={{ gridColumn: "1 / -1" }}>
                  <h4 style={{ margin: "8px 10px" }}>OOGT</h4>
                  <table className="table table--dense">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th>General Pattern</th>
                        <th>Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.oogt || []).map((r, i) => (
                        <tr key={i}>
                          <td>{r.Parameter}</td>
                          <td>{r["General Pattern"]}</td>
                          <td>{r.Current}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="hstack" style={{ gap: 8 }}>
                {/*<button className="primary" onClick={confirmSave}>Confirm & Save</button>*/}
                <button
                  className="primary"
                  onClick={() => {
                    setPendingAction(() => confirmSave);
                    setPinOpen(true);
                  }}
                >
                  Confirm & Save
                </button>
                <button className="ghost" onClick={() => setPreview(null)}>
                  Go Back (edit more)
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <ModalShell
        open={invalidateModal.open}
        onClose={() => setInvalidateModal({ open: false, comments: "" })}
        title="Invalidate lot — add comments"
        subtitle="Comments are optional but useful for the vendor when re-uploading the corrected COA."
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setInvalidateModal({ open: false, comments: "" })}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={continueInvalidateWithPin}
            >
              Continue
            </button>
          </>
        }
      >
        <label>
          Comments
          <textarea
            rows={5}
            value={invalidateModal.comments}
            onChange={(e) =>
              setInvalidateModal((current) => ({
                ...current,
                comments: e.target.value,
              }))
            }
            placeholder="Reason for sending this lot back to the vendor for re-upload"
          />
        </label>
      </ModalShell>

      <PinModal
        uid={user?.uid}
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onVerified={async () => {
          setPinOpen(false);
          if (pendingAction) await pendingAction();
        }}
      />
    </div>
  );
}
